import { proxyCustomElement, HTMLElement, h } from '@stencil/core/internal/client';
import { a as extractElementsFromSlot } from './slot-utils.js';
import { l as luxon } from './luxon.js';

const participantTimelineCss = ".timeline{position:relative;display:grid;grid-template-areas:\"blank times\" \"ids tracks\";grid-template-columns:auto 1fr;font:var(--tt-font-medium);padding:2rem;border-radius:0.5rem;border:1px solid var(--tt-color-gray-200)}.timeline .time{grid-area:times;position:relative;height:3rem}.timeline .time .t{position:absolute;transform:translate(-50%)}.timeline .time .t::after{content:\"\";height:1.5rem;width:1px;background-color:var(--tt-color-gray-400);position:absolute;top:100%;left:calc(50% - 0.5px)}.timeline .days{position:relative;z-index:2;overflow:hidden;margin-block:-0.75rem;pointer-events:none;grid-column:2;grid-row:1/-1}.timeline .days .d{position:absolute;width:0px;height:100%;border-left:1px solid var(--tt-color-blue-300-50);transform:translateX(-0.5px);white-space:nowrap;color:var(--tt-color-gray-500);padding-left:0.25rem;font:var(--tt-font-standard)}.timeline .tracks{grid-row:2;grid-column:1/-1;display:grid;flex-direction:column;grid-template-columns:subgrid}.timeline .tracks .track{display:grid;grid-column:1/-1;grid-template-columns:subgrid;border-block:1px solid var(--tt-color-gray-100);padding-block:0.125rem;margin-bottom:-1px}.timeline .tracks .track .track-name{grid-area:ids;border-right:1px solid var(--tt-color-gray-50);text-align:right;padding-inline:2rem 0.5rem}.timeline .tracks .track .track-slot{grid-area:tracks;flex:1;position:relative;display:flex;flex-direction:column;overflow:hidden;row-gap:1px}.timeline .tracks .track .track-slot .track-item{height:1rem}.timeline .cursor{position:absolute;top:0;height:100%;width:1px;transform:translateX(-1px);background-color:var(--tt-color-blue-300);pointer-events:none}";

const ParticipantTimeline = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.zoom = [0, 1];
    this.cursor = undefined;
    this.boundaries = [+Infinity, -Infinity];
    this.tracks = new Map();
    this.flag = 0;
    this.timezone = 'UTC';
  }
  getRelativeOffsetInternal(...times) {
    const { min, interval } = this.getFullInterval();
    return times.map((t) => {
      const tn = typeof t === 'string' ? new Date(t).getTime() : t;
      return (tn - min) / interval;
    });
  }
  /** Get the relative offset [0–1] of a timestamp in relation to the displayed timeline. Accepts string dates or epoch numbers */
  async getRelativeOffset(...times) {
    return this.getRelativeOffsetInternal(...times);
  }
  /** Force track elements to recalculate their positioning */
  async updateAll() {
    this.element.querySelectorAll('tt-participant-timeline-track').forEach((el) => el.update());
  }
  handlePropChange() {
    this.updateAll();
  }
  getFullInterval() {
    const interval = this.boundaries[1] - this.boundaries[0];
    const padding = 0.02;
    const intervalPadding = interval * padding;
    const min = this.boundaries[0] - intervalPadding;
    const max = this.boundaries[1] + intervalPadding;
    const paddedInterval = interval * (1 + padding * 2);
    return {
      min: min + this.zoom[0] * paddedInterval,
      max: max + this.zoom[1] * paddedInterval,
      interval: paddedInterval * (this.zoom[1] - this.zoom[0]),
    };
  }
  roundToNearestAcceptable(n) {
    const ACCEPTABLE_VALUES = [
      1, 2, 5, 10, 15, 20, 30, 60, 90, 120, 240, 300, 600, 900, 1200, 1800, 3600, 5400, 7200, 18000, 36000, 43200,
      86400, 172800, 432000,
    ];
    for (let i = 1; i < ACCEPTABLE_VALUES.length; ++i) {
      if (ACCEPTABLE_VALUES[i] / n > n / ACCEPTABLE_VALUES[i - 1])
        return ACCEPTABLE_VALUES[i - 1];
    }
    return ACCEPTABLE_VALUES[ACCEPTABLE_VALUES.length - 1];
  }
  getTimeIntervals() {
    var _a;
    const availableWidth = (_a = this.timeMarkersElement) === null || _a === void 0 ? void 0 : _a.offsetWidth;
    if (!availableWidth)
      return [];
    const { min, interval } = this.getFullInterval();
    const roughDivisions = Math.max(Math.floor(availableWidth / 100), 1);
    const divisionSize = this.roundToNearestAcceptable(interval / roughDivisions / 1000) * 1000;
    const roundMin = Math.round(min / divisionSize) * divisionSize;
    const divisions = Math.floor(interval / divisionSize);
    return new Array(divisions + 1).fill(0).map((_, idx) => Math.round(roundMin + idx * divisionSize));
  }
  getDayIntervals() {
    const { min, max } = this.getFullInterval();
    if (!Number.isFinite(min) || !Number.isFinite(max))
      return [];
    const startDay = luxon.DateTime.fromMillis(min).setZone(this.timezone).startOf('day');
    const endDay = luxon.DateTime.fromMillis(max).setZone(this.timezone).startOf('day');
    const days = endDay.diff(startDay).as('days');
    if (days <= 0)
      return [];
    return new Array(days).fill(0).map((_, idx) => startDay.plus({ day: idx + 1 }).toMillis());
  }
  formatTime(unixTimeMillis) {
    const t = luxon.DateTime.fromMillis(unixTimeMillis).setZone(this.timezone);
    return t.toISOTime({
      suppressMilliseconds: true,
      suppressSeconds: true,
      extendedZone: false,
      includeOffset: false,
      includePrefix: false,
    });
    // return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }
  getCursorPosition() {
    var _a, _b;
    if (this.cursor === undefined)
      return;
    const fullRect = (_a = this.timelineElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    const timeRect = (_b = this.timeMarkersElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
    if (!timeRect)
      return 0;
    if (!fullRect)
      return timeRect.x;
    return timeRect.x - fullRect.x + this.cursor * timeRect.width;
  }
  getCursorTime() {
    if (this.cursor === undefined)
      return '';
    const { min, interval } = this.getFullInterval();
    return this.formatTime(min + this.cursor * interval);
  }
  handleMouseMove(e) {
    var _a;
    const timeRect = (_a = this.timeMarkersElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    if (timeRect) {
      const d = Math.max(0, e.x - timeRect.x);
      this.cursor = Math.min(1, d / timeRect.width);
    }
  }
  handleMouseWheel(e) {
    if (this.cursor === undefined)
      return;
    e.preventDefault();
    if (e.deltaY !== 0) {
      const range = this.zoom[1] - this.zoom[0];
      const nexp = Math.abs(e.deltaY) >= 60 ? 0.25 * e.deltaY : e.deltaY;
      const newRange = range * Math.pow(1.01, -nexp);
      const delta = newRange - range;
      const newZoom = [
        Math.max(0, this.zoom[0] + delta * this.cursor),
        Math.min(1, this.zoom[1] - delta * (1 - this.cursor)),
      ];
      if (newZoom[1] - newZoom[0] > 0.001)
        this.zoom = newZoom;
    }
    if (Math.abs(e.deltaX) > 1) {
      const range = this.zoom[1] - this.zoom[0];
      const delta = range * 0.002 * e.deltaX;
      const newXZoom = Math.max(0, this.zoom[0] + delta);
      this.zoom = [newXZoom, newXZoom + range];
    }
    this.updateAll();
  }
  handleMouseLeave() {
    this.cursor = undefined;
  }
  async handleSlotChange(event) {
    const newTracks = extractElementsFromSlot(event.target, 'tt-participant-timeline-track');
    await Promise.all(newTracks.map(async (t) => {
      var _a;
      const { id, joinTime } = await t.getIdentityInfo();
      if (this.tracks.has(id)) {
        (_a = this.tracks.get(id)) === null || _a === void 0 ? void 0 : _a.add(joinTime);
      }
      else {
        this.tracks.set(id, new Set([joinTime]));
      }
      const [start, end] = await t.getTime();
      if (start < this.boundaries[0])
        this.boundaries[0] = start;
      if (end > this.boundaries[1])
        this.boundaries[1] = end;
      ++this.flag;
      t.slot = `${id}|${joinTime}`;
    }));
    this.updateAll();
  }
  connectedCallback() {
    this.getRelativeOffsetInternal = this.getRelativeOffsetInternal.bind(this);
    this.getRelativeOffset = this.getRelativeOffset.bind(this);
    this.updateAll = this.updateAll.bind(this);
    this.getFullInterval = this.getFullInterval.bind(this);
    this.getTimeIntervals = this.getTimeIntervals.bind(this);
    this.getDayIntervals = this.getDayIntervals.bind(this);
    this.formatTime = this.formatTime.bind(this);
    this.getCursorPosition = this.getCursorPosition.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseWheel = this.handleMouseWheel.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleSlotChange = this.handleSlotChange.bind(this);
    this.handlePropChange = this.handlePropChange.bind(this);
  }
  render() {
    return (h("div", { class: "timeline", part: "base", ref: (el) => (this.timelineElement = el), onWheel: this.handleMouseWheel, onMouseMove: this.handleMouseMove, onMouseLeave: this.handleMouseLeave }, h("div", { class: "time", ref: (el) => (this.timeMarkersElement = el) }, this.getTimeIntervals().map((t) => {
      const [offset] = this.getRelativeOffsetInternal(t);
      return (h("div", { class: "t", style: { left: `${offset * 100}%` } }, this.formatTime(t)));
    })), h("div", { class: "days" }, this.getDayIntervals().map((d) => {
      const [offset] = this.getRelativeOffsetInternal(d);
      return (h("div", { class: "d", style: { left: `${offset * 100}%` } }, luxon.DateTime.fromMillis(d).toFormat('LLL dd')));
    })), h("div", { class: "tracks" }, Array.from(this.tracks.entries()).map(([id, tracks]) => (h("div", { class: "track" }, h("div", { class: "track-name" }, id), h("div", { class: "track-slot" }, Array.from(tracks.values()).map((t) => (h("div", { class: "track-item" }, h("slot", { name: `${id}|${t}` }))))))))), this.cursor !== undefined && (h("div", { class: "cursor", style: { left: `${this.getCursorPosition()}px` } }, this.getCursorTime())), h("slot", { onSlotchange: this.handleSlotChange })));
  }
  get element() { return this; }
  static get watchers() { return {
    "timezone": ["handlePropChange"]
  }; }
  static get style() { return participantTimelineCss; }
}, [1, "tt-participant-timeline", {
    "timezone": [1],
    "zoom": [32],
    "cursor": [32],
    "boundaries": [32],
    "tracks": [32],
    "flag": [32],
    "getRelativeOffset": [64],
    "updateAll": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-participant-timeline"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-participant-timeline":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ParticipantTimeline);
      }
      break;
  } });
}

const TtParticipantTimeline = ParticipantTimeline;
const defineCustomElement = defineCustomElement$1;

export { TtParticipantTimeline, defineCustomElement };
